/**
 * IMPORTS
 */
import {gql} from '@apollo/client';


/**
 * EXPORTS
 */

/**
 * I am a query to fetch available agents.
 */
export const FETCH_AGENTS = gql`
query Agents ($company: Int!) {
  agents(company: $company) {
    away
    connected
    fullname
    paused
    standby
    uid
  }
}
`;


/**
 * I am a query to fetch agents messages metrics.
 */
export const FETCH_AGENTS_MESSAGES_METRICS = gql`
  query messagesMetrics (
    $agents: [String]!,
    $conversations: [Int]!,
    $from: Int!,
    $to: Int!,
    $type: Int!
  ) {
    result: messagesMetrics (
      agents: $agents,
      conversations: $conversations,
      from: $from,
      to: $to,
      type: $type
    ) {
      ... on MessagesMetricsResult {
        __typename
        messagesMetrics: rows {
          uid
          messages
        }
      }

      ... on Error {
        __typename
        code
        info
      }
    }
  }
`;


/**
 * I am a query to fetch ongoing conversations of contacts.
 */
export const FETCH_CONTACTS_ONGOING = gql`
query ContactsOngoing ($contacts: [String!]!) {
  conversations: ongoingContactsConversations (contacts: $contacts) {
    account {
      channel
      id
    }
    contact {
      id
      name
      uid
      username
    }
    end
    hasAgentAnswer
    id
    lastInteraction
    start
    subject
    unansweredMessages
  }
}
`;


/**
 * I am a query to fetch a conversation.
 */
export const FETCH_CONVERSATION = gql`
query Conversation ($company: Int!, $id: Int!) {
  conversation(company: $company, id: $id) {
    contact {
      uid
    }
    hasAgentAnswer
    messages {
      ack
      id
      body
      caption
      deleted
      mode
      originalId
      outgoing
      referral
      reply
      timestamp
      type
    }
  }
}
`;


/**
 * I am a query to fetch a conversation data.
 */
export const FETCH_CONVERSATION_DATA = gql`
query Conversation ($company: Int!, $id: Int!) {
  conversation(company: $company, id: $id) {
    account {
      channel
      id
    }
    agentFinished
    contact {
      id
      name
      uid
      username
    }
    end
    id
    lastInteraction
    start
    subject
    unansweredMessages
  }
}
`;


/**
 * I am a query to fetch a conversation app.
 */
export const FETCH_CONVERSATION_APP = gql`
query Conversation ($company: Int!, $id: Int!) {
    conversation(company: $company, id: $id) {
        application {
            type
            uid
        }
    }
}
`;


/**
 * I am a query to fetch the conversation flow.
 */
export const FETCH_CONVERSATION_FLOW = gql`
query conversationFlow ($conversation: Int!, $size: Int!, $offset: Int!) {
  result: conversationFlow (
    conversation: $conversation,
    size: $size,
    offset: $offset,
  ) {
    ... on ConversationFlows {
      __typename
      flow: rows {
        accountId
        app
        end
        id
        start
        status
        uid
      }
    }

    ... on Error {
      __typename
      code
      info
    }
  }
}
`;


/**
 * I am a query to fetch conversations by ids.
 */
export const FETCH_CONVERSATIONS_BY_IDS = gql`
query conversationsByIds($accounts: [Int!]!, $conversations: [Int!]!) {
    result: conversationsByIds(
        accounts: $accounts,
        conversations: $conversations,
    ) {
        __typename

        ... on ConversationsByIdsResult {
            conversations: rows {
                account
                agentFinished
                contact
                contactName
                contactUid
                direction
                end
                endApp
                endStatus
                finished
                firstAgent
                firstAnswer
                id
                lastApp
                lastStatus
                lastUid
                originalId
                queue
                result
                start
                subject
            }
        }

        ... on Error {
            code
            info
        }
    }
}
`;


/**
 * I am a query to fetch conversations report.
 */
export const FETCH_CONVERSATIONS_REPORT = gql`
query conversationsReport ($accounts: [Int!]!, $from: Int!, $to: Int!) {
  result: conversationsReport(accounts: $accounts, from: $from, to: $to) {
    ... on ConversationsReportResult {
      __typename
      metrics: rows {
        account
        agentFinished
        contactUid
        direction
        end
        finished
        firstAgent
        firstAnswer
        id
        lastApp
        lastStatus
        lastUid
        queue
        start
      }
    }

    ... on Error {
      __typename
      code
      info
    }
  }
}
`;


/**
 * I am a query to fetch an agent's conversations.
 */
export const FETCH_AGENT_CONVERSATIONS = gql`
query conversations ($agent: String!, $company: Int!, $from: Int!, $to: Int!) {
  conversations(agent: $agent, company: $company, from: $from, to: $to) {
    id
    account {
      id
      channel
    }
    contact {
      id
      name
      uid
      username
    }
    end
    lastInteraction
    start
    subject
    unansweredMessages
  }
}
`;


/**
 * I am a query to fetch a message by original id.
 */
export const FETCH_MESSAGE_BY_ORIGINAL_ID = gql`
query messageByOriginalId ($originalId: String!) {
  messageByOriginalId(originalId: $originalId) {
    __typename
    ... on MessageByOriginalId {
      message {
        ack
        body
        caption
        deleted
        id
        mode
        originalId
        outgoing
        referral
        reply
        timestamp
        type
      }
    }
    ... on Error {
      code
      info
    }
  }
}
`;


/**
 * I am a query to fetch an agent's ongoing conversations.
 */
export const FETCH_ONGOING_CONVERSATIONS = gql`
query ongoingConversations ($agent: String!, $company: Int!) {
  ongoingConversations(agent: $agent, company: $company) {
    id
    account {
      id
      channel
    }
    contact {
      id
      name
      uid
      username
    }
    dynamic
    end
    hasAgentAnswer
    lastInteraction
    start
    subject
    unansweredMessages
  }
}
`;


/**
 * I am a query to fetch a conversation's page.
 */
export const FETCH_PAGE = gql`
query conversationsPage ($company: Int!,
                         $page: Int!,
                         $session: String!,
                         $size: Int!) {
  conversationsPage(company: $company,
                    page: $page,
                    session: $session,
                    size: $size) {
    account {
      channel
    }
    contact {
      id
      name
      uid
      username
    }
  }
}
`;


/**
 * I am a query to fetch quality report.
 */
export const FETCH_QUALITY_REPORT = gql`
query QualityReport(
  $from: Int!
  $to: Int!
  $accounts: [Int!]
  $agent: String
  $queue: String
  $score: [Int!]
) {
  metrics: conversationsQualityReport(
    from: $from
    to: $to
    accounts: $accounts
    agent: $agent
    queue: $queue
    score: $score
  ) {
    __typename

    ... on ConversationsQualityReportResult {
      rows {
        analysis {
          score
        }

        conversation {
          account
          agentFinished
          contactUid
          direction
          end
          firstAgent
          id
          lastApp
          lastUid
          start
        }
      }
    }

    ... on Error {
      code
      info
    }
  }
}
`;


/**
 * I am a query to fetch real time metrics.
 */
export const FETCH_REALTIME_METRICS = gql`
query realTimeMetrics {
  metrics: conversationsStatus {
    ... on ConversationsStatus {
      __typename
      accounts {
        attending
        chatbot
        conversations {
          attending
          chatbot
          hasAgentAnswer
          incoming
          outgoing
          pending
          queued
          total
        }
        hasAgentAnswer
        id
        incoming
        outgoing
        pending
        queued
        total
      }
      apps {
        attending
        conversations {
          attending
          hasAgentAnswer
          incoming
          outgoing
          pending
          queued
          total
        }
        hasAgentAnswer
        incoming
        outgoing
        pending
        queued
        total
        type
        uid
      }
      attending {
        conversations {
          hasAgentAnswer
          incoming
          outgoing
          total
        }
        hasAgentAnswer
        incoming
        outgoing
        total
      }
      chatbot {
        conversations {
          hasAgentAnswer
          incoming
          outgoing
          total
        }
        hasAgentAnswer
        incoming
        outgoing
        total
      }
      conversations {
        hasAgentAnswer
        incoming
        outgoing
        total
      }
      hasAgentAnswer
      incoming
      outgoing
      queued {
        conversations {
          hasAgentAnswer
          incoming
          outgoing
          total
        }
        hasAgentAnswer
        incoming
        outgoing
        total
      }
      total
    }

    ... on Error {
      __typename
      code
      info
    }
  }
}
`;


/**
 * I am a query to check whether template is needed to send message to contact.
 */
export const NEED_TEMPLATE = gql`
query needTemplate ($account: String!, $contact: String!) {
  needTemplate(account: $account, contact: $contact)
}
`;
